/**
 * Service logs: `buildingServices/servicesLogs/{year}/{serviceType}/{buildingKey}/{logId}`,
 * written by iOS when a service is done at a building ({user, date, dateString, value?, comment?}).
 *
 * An admin can correct `value` on the web (Analys). Each correction appends
 * `corrections/{pushKey}` in the same update as the new value; entries are never edited or removed.
 */

/** Where one log entry lives. */
export interface ServiceLogRef {
    year: string;
    serviceType: string;
    buildingKey: string;
    logId: string;
}

export interface ServiceLogCorrection {
    key: string;
    from: number;
    to: number;
    /** UID */
    changedBy: string;
    /** Stockholm `YYYY-MM-DD hh:mm:ss` */
    changedWhen: string;
    reason?: string;
}

export const serviceLogPath = ({ year, serviceType, buildingKey, logId }: ServiceLogRef): string =>
    `buildingServices/servicesLogs/${year}/${serviceType}/${buildingKey}/${logId}`;

const isFiniteNumber = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);

/** Parses `corrections`, oldest first; invalid entries are skipped. */
export const parseServiceLogCorrections = (data: unknown): ServiceLogCorrection[] => {
    if (!data || typeof data !== 'object') return [];
    const corrections: ServiceLogCorrection[] = [];
    Object.entries(data as Record<string, unknown>).forEach(([key, raw]) => {
        const item = raw as Record<string, unknown> | null;
        if (!item || !isFiniteNumber(item.from) || !isFiniteNumber(item.to)
            || typeof item.changedBy !== 'string' || typeof item.changedWhen !== 'string') {
            console.warn(`Ogiltig korrigering ${key} i tjänstelogg, hoppas över`);
            return;
        }
        corrections.push({
            key,
            from: item.from,
            to: item.to,
            changedBy: item.changedBy,
            changedWhen: item.changedWhen,
            ...(typeof item.reason === 'string' && item.reason ? { reason: item.reason } : {}),
        });
    });
    return corrections.sort((a, b) => a.changedWhen.localeCompare(b.changedWhen) || a.key.localeCompare(b.key));
};

/** Swedish error for an invalid corrected value, or null when it can be saved. */
export const serviceLogValueError = (value: number, current: number): string | null => {
    if (!Number.isFinite(value)) return 'Ange ett tal';
    if (value < 0) return 'Värdet kan inte vara negativt';
    if (value === current) return 'Värdet är oförändrat';
    return null;
};
